import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Section } from "../components/Common";
import AuthBrandHeader from "../components/AuthBrandHeader";
import { toast } from "sonner";
import { errorMessage } from "../lib/errors";

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loginEmail, startGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Surface OAuth errors (e.g. user cancelled Google) once, then clean the URL
  useEffect(() => {
    const err = searchParams.get("oauth_error");
    if (err) {
      toast.error(t("auth.oauth_error", "Google sign-in was cancelled or failed"));
      const next = new URLSearchParams(searchParams);
      next.delete("oauth_error");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginEmail(email, password);
      toast.success(t("auth.welcome_back_toast"));
      const next = searchParams.get("next");
      navigate(next && next.startsWith("/") ? next : "/profile");
    } catch (e) {
      toast.error(errorMessage(e, t("auth.login_failed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthBrandHeader
        eyebrow={t("auth.login_eyebrow")}
        title={t("auth.login_title")}
        subtitle={t("auth.login_subtitle")}
      />
      <Section>
      <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
        <div className="card-surface p-6" data-testid="login-form">
          <form onSubmit={submit} className="grid gap-3">
            <input type="email" placeholder={t("auth.email")} value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="login-email" />
            <input type="password" placeholder={t("auth.password")} value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="login-password" />
            <button type="submit" disabled={loading} className="btn-primary text-sm" data-testid="login-submit">{loading ? "…" : t("auth.sign_in")}</button>
          </form>
          <div className="mt-4 flex justify-between text-sm text-white/70">
            <Link to="/auth/reset" data-testid="login-forgot">{t("auth.forgot")}</Link>
            <Link to={`/signup${searchParams.get("next") ? `?next=${encodeURIComponent(searchParams.get("next"))}` : ""}`} data-testid="login-to-signup">{t("auth.no_account")} {t("auth.sign_up")}</Link>
          </div>
          <p className="mt-4 text-xs text-white/55 leading-relaxed" data-testid="login-legal-ack">
            {t("auth.legal_ack_prefix", "By continuing, you agree to the")}{" "}
            <Link
              to="/terms"
              className="underline underline-offset-2 decoration-white/30 hover:text-white hover:decoration-white/60"
              data-testid="login-legal-terms"
            >
              {t("auth.legal_ack_terms", "Terms of Use")}
            </Link>{" "}
            {t("auth.legal_ack_conjunction", "and acknowledge the")}{" "}
            <Link
              to="/privacy"
              className="underline underline-offset-2 decoration-white/30 hover:text-white hover:decoration-white/60"
              data-testid="login-legal-privacy"
            >
              {t("auth.legal_ack_privacy", "Privacy Policy")}
            </Link>
            {t("auth.legal_ack_suffix", ".")}
          </p>
        </div>
        <div className="card-surface p-6 flex flex-col gap-3">
          <div className="text-xs uppercase tracking-[0.24em] gold-text">{t("nav.sso")}</div>
          <button onClick={startGoogle} className="btn-secondary text-sm inline-flex items-center justify-center gap-2" data-testid="google-login">
            <svg width="16" height="16" viewBox="0 0 533.5 544.3" xmlns="http://www.w3.org/2000/svg"><path fill="#4285f4" d="M533.5 278.4c0-18.5-1.7-36.4-4.7-53.8H272v101.7h146.9c-6.3 34-25.4 62.7-54 81.9v68h87.2c51-47 81.4-116.2 81.4-197.8z"/><path fill="#34a853" d="M272 544.3c73.6 0 135.3-24.4 180.4-66.1l-87.2-68c-24.2 16.2-55 25.7-93.2 25.7-71.6 0-132.3-48.3-154-113.3H28.1v70.9c45.1 89.5 137.6 150.8 243.9 150.8z"/><path fill="#fbbc04" d="M118 322.5c-10.5-31-10.5-64.5 0-95.5V156.1H28.1c-39 78-39 169.7 0 247.7L118 322.5z"/><path fill="#ea4335" d="M272 107.7c39.9-.6 78.1 14 107.4 41.5l80.4-80.4C407 24.5 340.5-.4 272 0 165.7 0 73.2 61.3 28.1 150.9l89.9 70.9c21.7-65 82.4-114.1 154-114.1z"/></svg>
            {t("auth.google")}
          </button>
          <p className="text-xs text-white/55">{t("auth.sso_note")}</p>
        </div>
      </div>
    </Section>
    </>
  );
}
